/* eslint-disable camelcase */


import { ExamCenter } from "./examCenter";
import { User } from "./user";
import { Exam } from "./exam";
import { ExamSessionComment } from "./examSessionComment";


export interface ExamSessionCreateOrUpdateData {
    code: string;
    center_id: number;
    supervisor_id?: number;
    correcteur_id?: number;
    correcteur_2_id?: number;
    date: string;
    convocation_time: string;
    zoom_link?: string;
    exam_location_details?: string;
    info?: string;
    notify_users: boolean;
    nb_max_candidates?: number;
}


class Preregistration {
    id: number;
    firstname: string;
    lastname: string;
    email: string;
    phoneNumber: string | null;
    exam: Exam | null = null;
    convocationSentAt: Date | null = null;

    constructor(data: any) {
        if (data) {
            this.id = data.id;
            this.firstname = data.first_name;
            this.lastname = data.last_name;
            this.email = data.email;

            if (data.phone_number) {
                this.phoneNumber = data.phone_number;
            }

            if (data.exam) {
                this.exam = new Exam(data.exam);

            }

            if (data.convocation_sent_at) {
                this.convocationSentAt = new Date(data.convocation_sent_at);
            }

        }
    }
}

export class ExamSession {
    id: number;
    code: string;
    center: ExamCenter;
    supervisor: User;
    correcteur: User;
    correcteur2: User;
    supervisorConfirmed: Date;
    correctorConfirmed: Date;
    corrector2Confirmed: Date;
    exams: Array<Exam>;
    // Local date with format 2022-05-19 I pefer not to parse into a Date or it will add timezones and that's a pain in the ass
    date: string;
    convocationTime: string;
    startDatetime: string;
    comments: Array<ExamSessionComment>;
    preregistrations: Array<Preregistration>;
    zoomLink: string | null;
    examLocationDetails: string | null;
    info: string | null;
    nbMaxCandidates: number | null;
    status: string;
    statusChangedAt: Date | null;

    constructor(data: any | ExamSession) {
        if (!data) {
            throw new Error("No data given");
        }

        if (data instanceof ExamSession) {
            this.id = data.id;
            this.code = data.code;
            this.center = data.center;
            if (data.supervisor) {
                this.supervisor = new User(data.supervisor);
            }
            if (data.correcteur) {
                this.correcteur = new User(data.correcteur);
            }
            if (data.correcteur2) {
                this.correcteur2 = new User(data.correcteur2);
            }
            this.supervisorConfirmed = data.supervisorConfirmed;
            this.correctorConfirmed = data.correctorConfirmed;
            this.corrector2Confirmed = data.corrector2Confirmed;
            if (data.exams) {
                this.exams = data.exams.map((e: Exam) => new Exam(e));
            }
            this.date = data.date;
            this.convocationTime = data.convocationTime;
            this.startDatetime = data.startDatetime;
            this.zoomLink = data.zoomLink;
            this.examLocationDetails = data.examLocationDetails;
            this.info = data.info;
            this.nbMaxCandidates = data.nbMaxCandidates;
            this.status = data.status;
            this.statusChangedAt = data.statusChangedAt;

        } else {

            if (!("id" in data)) {
                throw new Error("Session instanciated with undefined id");
            }

            if (!("start_datetime" in data)) {
                throw new Error("Session instanciated with undefined start_datetime");
            }

            if (!("code" in data)) {
                throw new Error("Session instanciated with undefined code");
            }

            if (!("center" in data)) {
                throw new Error("Session instanciated with undefined center");
            }

            if (!("date" in data)) {
                throw new Error("Session instanciated with undefined date");
            }

            this.id = data.id;
            this.code = data.code;
            this.center = new ExamCenter(data.center);
            this.date = data.date.substring(0, 16);
            this.startDatetime = data.start_datetime;
            this.nbMaxCandidates = data.nb_max_candidates;

            if (data.convocation_time) {
                this.convocationTime = data.convocation_time.substring(0, 5);
            }

            if (data.supervisor) {
                this.supervisor = new User(data.supervisor);
            }

            if (data.correcteur) {
                this.correcteur = new User(data.correcteur);
            }

            if (data.correcteur_2) {
                this.correcteur2 = new User(data.correcteur_2);
            }

            if (data.supervisor_confirmed) {
                this.supervisorConfirmed = new Date(data.supervisor_confirmed);
            }

            if (data.correcteur_confirmed) {
                this.correctorConfirmed = new Date(data.correcteur_confirmed);
            }

            if (data.correcteur_2_confirmed) {
                this.corrector2Confirmed = new Date(data.correcteur_2_confirmed);
            }

            if (data.exams) {
                this.exams = data.exams.map((e) => new Exam(e));
            }

            if (data.comments) {
                this.comments = data.comments.map((c: unknown) => new ExamSessionComment(c));
            }

            if (data.preregistrations) {
                this.preregistrations = data.preregistrations.map((p: unknown) => new Preregistration(p));
            }

            if (data.zoom_link) {
                this.zoomLink = data.zoom_link;
            }

            if (data.exam_location_details) {
                this.examLocationDetails = data.exam_location_details;
            }

            if (data.info) {
                this.info = data.info;
            }

            this.status = data.status || "planned";
            this.statusChangedAt = data.status_changed_at ? new Date(data.status_changed_at) : null;
        }
    }

    // Tickets dont le résultat est définitif : verdict saisi par le correcteur
    // puis verrouillé par le staff. Le taux de réussite ne porte que sur eux —
    // un verdict non verrouillé peut encore changer.
    private get validatedExams(): Array<Exam> {
        if (!this.exams) {
            return [];
        }

        return this.exams.filter((e: Exam) => e.success !== null && e.locked);
    }

    // Tickets corrigés que le staff n'a pas encore verrouillés : la part grise
    // du camembert, hors du taux.
    get nbAwaitingValidationExams(): number {
        if (!this.exams) {
            return 0;
        }

        return this.exams.filter((e: Exam) => e.success !== null && !e.locked).length;
    }

    // Tickets sans verdict : correction en attente, candidat absent ou ticket
    // jamais attribué. Ni part du camembert, ni ligne du taux — l'infobulle
    // seule les mentionne, faute de pouvoir distinguer ces trois cas.
    get nbUnresolvedExams(): number {
        if (!this.exams) {
            return 0;
        }

        return this.exams.length - this.nbPassedExams - this.nbAwaitingValidationExams;
    }

    get nbPassedExams(): number {
        return this.validatedExams.length;
    }

    get nbSuccessExams(): number {
        return this.validatedExams.filter((e: Exam) => e.success).length;
    }

    get nbFailedExams(): number {
        return this.nbPassedExams - this.nbSuccessExams;
    }

    get successRate(): number {
        if (!this.nbPassedExams) {
            return 0;
        }

        return this.nbSuccessExams / this.nbPassedExams;
    }

    // Infobulle de la colonne, une catégorie par ligne et sans recouvrement :
    // « 13 tickets : / 2 succès / 2 échecs / 3 à valider / 6 sans résultat »
    get examsSummary(): string {
        const total = this.exams ? this.exams.length : 0;
        const parts: Array<string> = [];

        if (this.nbSuccessExams > 0) {
            parts.push(`${this.nbSuccessExams} succès`);
        }

        if (this.nbFailedExams > 0) {
            parts.push(`${this.nbFailedExams} échec${this.nbFailedExams > 1 ? "s" : ""}`);
        }

        if (this.nbAwaitingValidationExams > 0) {
            parts.push(`${this.nbAwaitingValidationExams} à valider`);
        }

        if (this.nbUnresolvedExams > 0) {
            parts.push(`${this.nbUnresolvedExams} sans résultat`);
        }

        return [`${total} ticket${total > 1 ? "s" : ""} :`].concat(parts).join("\n");
    }


    get displayStatus(): string {
        if (this.status === "deleted") {
            return "Supprimé";
        }
        if (this.status === "incident") {
            return "Incident";
        }
        if (this.date && new Date(this.date) < new Date()) {
            return "Effectué";
        }
        return "Planifié";
    }

    get displayStatusClass(): string {
        if (this.status === "deleted") {
            return "badge-danger";
        }
        if (this.status === "incident") {
            return "badge-warning text-dark";
        }
        if (this.date && new Date(this.date) < new Date()) {
            return "badge-success";
        }
        return "badge-primary";
    }

    get isPast(): boolean {
        return !!this.date && new Date(this.date) < new Date();
    }

    get isToday(): boolean {
        if (!this.date) {
            return false;
        }
        const today = new Date();
        const todayStr = today.getFullYear() + "-" +
            String(today.getMonth() + 1).padStart(2, "0") + "-" +
            String(today.getDate()).padStart(2, "0");
        return this.date.substring(0, 10) === todayStr;
    }

    get wasOpen() {
        return this.startDatetime !== null;
    }

    get isOpen() {
        return this.wasOpen && !this.isFinished;
    }

    get isFinished() {
        if (!this.startDatetime) {
            return false;
        }

        const startDate = new Date(this.startDatetime),

            // Obtenir le timestamp actuel (en millisecondes)
            currentTimeStamp = new Date().getTime(),

            // Calculer la différence entre les deux timestamps
            timeDifference = currentTimeStamp - startDate.getTime(),

            // Durée d'une journée en millisecondes (24 heures)
            oneDayInMilliseconds = 24 * 60 * 60 * 1000;

        // Vérifier si la différence est supérieure à 24 heures
        return timeDifference > oneDayInMilliseconds;
    }

    get canClose() {
        return this.isOpen && this.exams.every((e: Exam) => e.success === null && e.can_pass === false);
    }

    // Le constructeur de copie ne recopie ni les préinscriptions ni les
    // vouchers : on protège tous les compteurs de remplissage contre un
    // tableau absent.
    private get preregistrationList(): Array<Preregistration> {
        return this.preregistrations || [];
    }

    private get examList(): Array<Exam> {
        return this.exams || [];
    }

    get nBPreinscriptions() {
        return this.preregistrationList.length;
    }

    get nbSentPreinscriptions() {
        return this.preregistrationList.filter((p) => p.convocationSentAt !== null).length;
    }

    // Remplissage de la session. Une place est engagée dès qu'un candidat est
    // derrière, que ce soit par une préinscription ou par un compte candidat.
    // Les trois compteurs sont disjoints et se lisent comme l'avancement du
    // cycle : saisi → compte créé → participation confirmée.
    //
    // Le stock de tickets posés sur la session mais pas encore attribués à une
    // préinscription n'est pas du remplissage : il reste hors barre (la colonne
    // voisine l'affiche déjà).

    /**
     * Vouchers engagés sur la session : ceux qui lui sont rattachés et ceux
     * portés par une de ses préinscriptions. Dédoublonnés par identifiant, un
     * voucher attribué à une préinscription figurant dans les deux listes.
     */
    private get sessionVouchers(): Array<Exam> {
        const byId = new Map<number, Exam>();

        this.examList.forEach((e) => byId.set(e.id, e));

        this.preregistrationList.forEach((p) => {
            if (p.exam != null) {
                byId.set(p.exam.id, p.exam);
            }
        });

        return Array.from(byId.values());
    }

    /** Candidats ayant créé leur compte via le lien de convocation. */
    get nbRegisteredCandidates(): number {
        return this.preregistrationList.filter((p) => p.exam != null && p.exam.student != null).length;
    }

    /** Rouge : candidat saisi, pas encore de compte candidat sur son voucher. */
    get nbUnvalidatedPreregistrations(): number {
        return this.preregistrationList.filter((p) => p.exam == null || p.exam.student == null).length;
    }

    /** Orange : compte candidat créé, participation pas encore confirmée. */
    get nbUnconfirmedAttendances(): number {
        return this.sessionVouchers.filter((e) => e.student != null && e.studentConfirmedAt == null).length;
    }

    /** Vert : compte candidat créé et participation confirmée. */
    get nbConfirmedAttendances(): number {
        return this.sessionVouchers.filter((e) => e.student != null && e.studentConfirmedAt != null).length;
    }

    /** Places engagées, les trois catégories réunies. */
    get nbEngagedSeats(): number {
        return this.nbUnvalidatedPreregistrations
            + this.nbUnconfirmedAttendances
            + this.nbConfirmedAttendances;
    }

    get hasCapacity(): boolean {
        return this.nbMaxCandidates > 0;
    }

    /** Gris : places totales moins les places engagées. */
    get nbRemainingSeats(): number {
        return Math.max(0, (this.nbMaxCandidates || 0) - this.nbEngagedSeats);
    }

    /**
     * Places engagées au-delà de la capacité déclarée. Sans capacité déclarée
     * il n'y a rien à dépasser : on ne crie pas au surbooking.
     */
    get nbOverbooking(): number {
        if (!this.hasCapacity) {
            return 0;
        }

        return Math.max(0, this.nbEngagedSeats - this.nbMaxCandidates);
    }

    /** Préinscrits dont la convocation n'est pas partie. */
    get nbToConvoke(): number {
        return Math.max(0, this.nBPreinscriptions - this.nbSentPreinscriptions);
    }

    /** Convoqués qui n'ont pas encore créé leur compte. */
    get nbToFollowUp(): number {
        return Math.max(0, this.nbSentPreinscriptions - this.nbRegisteredCandidates);
    }

    /** Une session surbookée sature la barre au lieu de la faire déborder. */
    private get fillingScale(): number {
        return Math.max(this.nbMaxCandidates || 0, this.nbEngagedSeats);
    }

    private fillingPercent(count: number): number {
        const scale = this.fillingScale;

        if (!scale) {
            return 0;
        }

        return (count / scale) * 100;
    }

    get pctUnvalidated(): number {
        return this.fillingPercent(this.nbUnvalidatedPreregistrations);
    }

    get pctUnconfirmed(): number {
        return this.fillingPercent(this.nbUnconfirmedAttendances);
    }

    get pctConfirmed(): number {
        return this.fillingPercent(this.nbConfirmedAttendances);
    }

    toString(): string {
        return this.code;
    }
}

export class ShortExamSession {
    id: number;
    code: string;

    constructor(data: any | ExamSession) {
        if (!data) {
            throw new Error("No data given");
        }

        if (data instanceof ExamSession) {
            this.id = data.id;
            this.code = data.code;

        } else {

            if (!("id" in data)) {
                throw new Error("Session instanciated with undefined id");
            }

            if (!("code" in data)) {
                throw new Error("Session instanciated with undefined code");
            }

            this.id = data.id;
            this.code = data.code;
        }
    }

    toString(): string {
        return this.code;
    }
}

/** Session proposée pour replanifier un ticket, avec son remplissage. */
export class RescheduleExamSession {
    id: number;
    code: string;
    // Date locale (même format que ExamSession.date), sans conversion de fuseau
    date: string;
    convocationTime: string | null;
    centerName: string;
    isRemote: boolean;
    nbMaxCandidates: number;
    nbExams: number;

    constructor(data: any) {
        this.id = data.id;
        this.code = data.code;
        this.date = data.date.substring(0, 16);
        this.convocationTime = data.convocation_time ? data.convocation_time.substring(0, 5) : null;
        this.centerName = data.center.nom;
        this.isRemote = data.center.master_remote;
        this.nbMaxCandidates = data.nb_max_candidates;
        this.nbExams = data.nb_exams;
    }

    get isFull(): boolean {
        return this.nbMaxCandidates > 0 && this.nbExams >= this.nbMaxCandidates;
    }
}
